import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  ChevronLeft, 
  Clock, 
  MapPin, 
  TrendingUp, 
  TrendingDown,
  AlertTriangle,
  Info,
  Lock,
  Shield,
  Users,
  Activity,
  History,
  AlertCircle,
  Target,
  CheckCircle2,
  XCircle,
  Radio,
  Loader2,
  Sparkles,
  BarChart3,
  FileText,
  Zap,
  Calendar,
  Gauge,
  Brain,
  Trophy,
  Lightbulb,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import LearningInsightsPanel from '../components/LearningInsightsPanel';
import PlayerPerformanceCard from '../components/PlayerPerformanceCard';
import PitcherMatchupCard from '../components/PitcherMatchupCard';
import { useSEO, generateGameDescription, SEO_KEYWORDS } from '../utils/seo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GameDetail() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isSubscriber } = useAuth();
  const location = window.location;
  
  // Get state from navigation (board position or stats page)
  const locationState = window.history.state?.usr;
  const fromBoard = locationState?.fromBoard;
  const returnTo = locationState?.returnTo;
  const returnContext = locationState; // Full context for stats page return
  
  // Get tab from URL query param
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(urlTab || 'overview');

  // Handle back navigation - use browser history for true "back" behavior
  const handleBackNavigation = () => {
    // Use browser history to go back to the exact previous page
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback if no history - go to board
      navigate('/board', { state: { scrollToGame: gameId } });
    }
  };
  
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPrediction, setUserPrediction] = useState(null);
  const [predictingFor, setPredictingFor] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [liveScore, setLiveScore] = useState(null);
  const [postGameAnalysis, setPostGameAnalysis] = useState(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [teamInjuries, setTeamInjuries] = useState({ home: [], away: [] });
  const [loadingInjuries, setLoadingInjuries] = useState(false);
  const [predictionDetails, setPredictionDetails] = useState(null);
  const [showPredictionModal, setShowPredictionModal] = useState(false);
  
  // Player prediction modal state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerPrediction, setPlayerPrediction] = useState(null);
  const [loadingPlayerPrediction, setLoadingPlayerPrediction] = useState(false);
  const [showPlayerPredictionModal, setShowPlayerPredictionModal] = useState(false);
  
  // New state for trends, schedule, confidence
  const [trendsData, setTrendsData] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [scheduleData, setScheduleData] = useState(null);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [confidenceData, setConfidenceData] = useState(null);
  const [loadingConfidence, setLoadingConfidence] = useState(false);
  
  // AI Learning Insights state
  const [aiLearningInsights, setAiLearningInsights] = useState(null);

  // SEO for game detail page - dynamic based on game data
  const gameForSEO = gameData?.game;
  useSEO({
    title: gameForSEO 
      ? `${gameForSEO.away_team?.name || 'Away'} vs ${gameForSEO.home_team?.name || 'Home'} Prediction | ${gameForSEO.league_id?.toUpperCase()}`
      : 'Game Prediction & Analysis',
    description: gameForSEO ? generateGameDescription(gameForSEO) : 'Get AI-powered game predictions with win probability, injury reports, and player matchups.',
    keywords: `${gameForSEO?.league_id || ''} prediction, ${gameForSEO?.home_team?.name || ''} vs ${gameForSEO?.away_team?.name || ''}, sports prediction, who will win, ${SEO_KEYWORDS.gameDetail}`,
    canonicalUrl: `https://sportsanalysisab.ca/game/${gameId}`
  });
  
  // Update active tab when URL changes
  useEffect(() => {
    if (urlTab && ['overview', 'trends', 'prediction', 'h2h', 'injuries', 'schedule', 'confidence'].includes(urlTab)) {
      setActiveTab(urlTab);
    }
    
    // Handle scroll to players section when coming back from player detail
    const scrollTo = searchParams.get('scrollTo');
    if (scrollTo === 'players' && urlTab === 'h2h') {
      setTimeout(() => {
        const playersSection = document.querySelector('[data-testid="h2h-player-stats"]');
        if (playersSection) {
          playersSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
    }
  }, [urlTab, searchParams]);

  useEffect(() => {
    const fetchGame = async () => {
      try {
        const response = await fetch(`${API_URL}/api/games/${gameId}`, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to load game data');
        }
        const data = await response.json();
        
        // Validate that we received proper game data
        if (!data || !data.game) {
          throw new Error('Invalid game data received');
        }
        
        setGameData(data);
        
        // Set AI Learning insights
        if (data.ai_learning_insights && !data.ai_learning_insights.locked) {
          setAiLearningInsights(data.ai_learning_insights);
        }
        
        if (data.game?.status === 'live') {
          setLiveScore({
            home_score: data.game.home_score || 0,
            away_score: data.game.away_score || 0,
            status_detail: data.game.status_detail || 'LIVE'
          });
        }
      } catch (err) {
        console.error('Error fetching game:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGame();
  }, [gameId]);

  // Poll for live scores every 30 seconds
  useEffect(() => {
    if (gameData?.game?.status !== 'live') return;
    
    const fetchLiveScore = async () => {
      try {
        const response = await fetch(`${API_URL}/api/games/live`);
        if (response.ok) {
          const data = await response.json();
          const liveGame = data.live_games?.find(g => g.game_id === gameId);
          if (liveGame) {
            setLiveScore({
              home_score: liveGame.home_score || 0,
              away_score: liveGame.away_score || 0,
              status_detail: liveGame.status_detail || 'LIVE'
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch live score:', err);
      }
    };

    fetchLiveScore();
    const interval = setInterval(fetchLiveScore, 30000);
    return () => clearInterval(interval);
  }, [gameId, gameData?.game?.status]);

  // Fetch user's prediction for this game
  useEffect(() => {
    const fetchUserPrediction = async () => {
      if (!isAuthenticated) return;
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/predictions/user/game/${gameId}`, {
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setUserPrediction(data.prediction);
        }
      } catch (err) {
        console.error('Failed to fetch user prediction:', err);
      }
    };
    fetchUserPrediction();
  }, [gameId, isAuthenticated]);

  // Fetch player stats for H2H analysis - now fetches game-specific players
  const [gamePlayers, setGamePlayers] = useState(null);
  
  // OPTIMIZED: Fetch all game-dependent data in parallel
  useEffect(() => {
    if (!gameData?.game?.game_id) return;
    
    const fetchAllGameData = async () => {
      const gameIdForFetch = gameData.game.game_id;
      
      // Set all loading states at once
      setLoadingStats(true);
      setLoadingInjuries(true);
      setLoadingTrends(true);
      setLoadingSchedule(true);
      setLoadingConfidence(true);
      
      // Fetch all data in parallel
      const [playersRes, injuriesRes, trendsRes, scheduleRes, confidenceRes] = await Promise.allSettled([
        fetch(`${API_URL}/api/players/game/${gameIdForFetch}`),
        fetch(`${API_URL}/api/games/${gameId}/injuries`),
        fetch(`${API_URL}/api/games/${gameId}/trends`),
        fetch(`${API_URL}/api/games/${gameId}/schedule-fatigue`),
        fetch(`${API_URL}/api/games/${gameId}/confidence-score`)
      ]);
      
      // Process results
      if (playersRes.status === 'fulfilled' && playersRes.value.ok) {
        const data = await playersRes.value.json();
        setGamePlayers(data);
      }
      setLoadingStats(false);
      
      if (injuriesRes.status === 'fulfilled' && injuriesRes.value.ok) {
        const data = await injuriesRes.value.json();
        setTeamInjuries({
          home: data.home_injuries || [],
          away: data.away_injuries || []
        });
      }
      setLoadingInjuries(false);
      
      if (trendsRes.status === 'fulfilled' && trendsRes.value.ok) {
        const data = await trendsRes.value.json();
        setTrendsData(data);
      }
      setLoadingTrends(false);
      
      if (scheduleRes.status === 'fulfilled' && scheduleRes.value.ok) {
        const data = await scheduleRes.value.json();
        setScheduleData(data);
      }
      setLoadingSchedule(false);
      
      if (confidenceRes.status === 'fulfilled' && confidenceRes.value.ok) {
        const data = await confidenceRes.value.json();
        setConfidenceData(data);
      }
      setLoadingConfidence(false);
    };
    
    fetchAllGameData();
  }, [gameData?.game?.game_id, gameId]);

  // Fetch post-game analysis for completed games (separate because conditional)
  useEffect(() => {
    const fetchPostGameAnalysis = async () => {
      if (gameData?.game?.status !== 'final') return;
      if (postGameAnalysis) return;
      
      setLoadingAnalysis(true);
      try {
        const response = await fetch(`${API_URL}/api/games/${gameId}/post-game-analysis`);
        if (response.ok) {
          const data = await response.json();
          setPostGameAnalysis(data.analysis);
        }
      } catch (err) {
        console.error('Failed to fetch post-game analysis:', err);
      } finally {
        setLoadingAnalysis(false);
      }
    };
    fetchPostGameAnalysis();
  }, [gameId, gameData?.game?.status, postGameAnalysis]);

  // Fetch prediction details when clicking on prediction
  const fetchPredictionDetails = async () => {
    if (predictionDetails) {
      setShowPredictionModal(true);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/games/${gameId}/prediction-details`);
      if (response.ok) {
        const data = await response.json();
        setPredictionDetails(data);
        setShowPredictionModal(true);
      } else {
        console.error('Failed to fetch prediction details:', response.status);
        toast.error('Failed to load prediction details');
      }
    } catch (err) {
      console.error('Failed to fetch prediction details:', err);
      toast.error('Failed to load prediction details');
    }
  };

  // Fetch player stat prediction when clicking a player
  const fetchPlayerPrediction = async (player, teamType) => {
    const playerId = player.player_id || player.id;
    if (!playerId) {
      toast.error('Player ID not available');
      return;
    }
    
    setSelectedPlayer({ ...player, teamType });
    setShowPlayerPredictionModal(true);
    setLoadingPlayerPrediction(true);
    setPlayerPrediction(null);
    
    try {
      const leagueId = gameData?.game?.league_id || 'nba';
      const response = await fetch(`${API_URL}/api/players/prediction/${playerId}?league_id=${leagueId}&game_id=${gameId}`);
      if (response.ok) {
        const data = await response.json();
        setPlayerPrediction(data);
      } else {
        console.error('Failed to fetch player prediction:', response.status);
        toast.error('Failed to load player prediction');
      }
    } catch (err) {
      console.error('Failed to fetch player prediction:', err);
      toast.error('Failed to load player prediction');
    } finally {
      setLoadingPlayerPrediction(false);
    }
  };

  // Get trend emoji/indicator
  const getTrendIndicator = (trend) => {
    if (trend === 'hot') return { emoji: '🔥', text: 'Hot', color: 'text-orange-400' };
    if (trend === 'cold') return { emoji: '❄️', text: 'Cold', color: 'text-blue-400' };
    return { emoji: '➡️', text: 'Normal', color: 'text-muted-foreground' };
  };

  const makePrediction = async (predictedWinner) => {
    if (!isAuthenticated) {
      toast.error('Sign in to make predictions');
      return;
    }
    
    setPredictingFor(predictedWinner);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/predictions/user`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify({
          game_id: gameId,
          predicted_winner: predictedWinner
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserPrediction({
          predicted_winner: predictedWinner,
          status: 'pending',
          agreed_with_platform: data.agreed_with_platform
        });
        toast.success('Prediction recorded!');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to record prediction');
      }
    } catch (err) {
      toast.error('Failed to record prediction');
    } finally {
      setPredictingFor(null);
    }
  };

  const formatMSTTime = (isoString) => {
    try {
      const date = new Date(isoString);
      // Format time in MST (Mountain Standard Time)
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/Denver'
      }) + ' MST';
    } catch (e) {
      return '';
    }
  };

  const formatMSTDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('en-US', { 
        weekday: 'short', 
        month: 'short', 
        day: 'numeric',
        timeZone: 'America/Denver'
      });
    } catch (e) {
      return '';
    }
  };

  const getConfidenceClass = (confidence) => {
    switch (confidence?.toLowerCase()) {
      case 'high': return 'confidence-high';
      case 'medium': return 'confidence-medium';
      case 'low': return 'confidence-low';
      default: return 'confidence-medium';
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 70) return 'text-green-400';
    if (score >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-32 mb-6" />
          <Skeleton className="h-64 w-full mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="h-96 col-span-2" />
            <Skeleton className="h-96" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !gameData) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold uppercase mb-2">Game Not Found</h2>
            <p className="text-muted-foreground mb-4">{error || 'Unable to load game data'}</p>
            <Button onClick={() => navigate('/board')} className="rounded-sm">
              Back to Board
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { game, drivers, trends, head_to_head, injuries, model_notes, is_premium_locked } = gameData;
  
  // Defensive check - if game is missing, show error
  if (!game) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-display text-2xl font-bold uppercase mb-2">Game Data Missing</h2>
            <p className="text-muted-foreground mb-4">Unable to load game details. Please try again.</p>
            <Button onClick={() => navigate('/board')} className="rounded-sm">
              Back to Board
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  const homeWinning = (game.home_probability || 50) > (game.away_probability || 50);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Back button */}
        <div className="border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <Button 
              variant="ghost" 
              className="rounded-sm gap-2"
              onClick={handleBackNavigation}
              data-testid="back-to-board-btn"
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="border-b border-border bg-card/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* League & Time */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
              <Badge variant="outline" className="font-mono text-xs uppercase">
                {game.league_name}
              </Badge>
              <Badge className={getConfidenceClass(game.confidence)}>
                {game.confidence} Confidence
              </Badge>
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                {formatMSTDate(game.start_time)} at {formatMSTTime(game.start_time)}
              </div>
              {game.venue && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  {game.venue}
                </div>
              )}
            </div>

            {/* Matchup */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
              {/* Away Team */}
              <div className="text-center lg:text-left">
                <div className="flex flex-col lg:flex-row items-center gap-4">
                  <div className="w-20 h-20 bg-secondary rounded-sm flex items-center justify-center">
                    {game.away_team?.logo_url ? (
                      <img 
                        src={game.away_team.logo_url} 
                        alt={game.away_team.name}
                        className="w-16 h-16 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="font-display font-bold text-2xl">
                        {game.away_team?.abbreviation}
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight">
                      {game.away_team?.name}
                    </h2>
                    <p className="text-muted-foreground">Away</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center lg:justify-start gap-2">
                  {!homeWinning && <TrendingUp className="w-5 h-5 text-green-400" />}
                  <span className={`font-mono text-4xl font-bold ${!homeWinning ? 'text-green-400' : 'text-muted-foreground'}`}>
                    {(game.away_probability || 50).toFixed(0)}%
                  </span>
                </div>
              </div>

              {/* VS / Probability Meter */}
              <div className="text-center">
                <span className="font-display text-3xl font-bold text-muted-foreground">VS</span>
                <div className="mt-4">
                  <div className="h-4 bg-secondary rounded-full overflow-hidden flex">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-400 transition-all duration-500"
                      style={{ width: `${game.away_probability}%` }}
                    />
                    <div 
                      className="bg-gradient-to-r from-green-400 to-green-500 transition-all duration-500"
                      style={{ width: `${game.home_probability}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-2">
                    <span>{game.away_team?.abbreviation}</span>
                    <span>{game.home_team?.abbreviation}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-4">
                  Uncertainty: {((game.uncertainty || 0) * 100).toFixed(0)}%
                </p>
              </div>

              {/* Home Team */}
              <div className="text-center lg:text-right">
                <div className="flex flex-col lg:flex-row-reverse items-center gap-4">
                  <div className="w-20 h-20 bg-secondary rounded-sm flex items-center justify-center">
                    {game.home_team?.logo_url ? (
                      <img 
                        src={game.home_team.logo_url} 
                        alt={game.home_team.name}
                        className="w-16 h-16 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="font-display font-bold text-2xl">
                        {game.home_team?.abbreviation}
                      </span>
                    )}
                  </div>
                  <div>
                    <h2 className="font-display text-2xl font-bold uppercase tracking-tight">
                      {game.home_team?.name}
                    </h2>
                    <p className="text-muted-foreground">Home</p>
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-center lg:justify-end gap-2">
                  {homeWinning && <TrendingUp className="w-5 h-5 text-green-400" />}
                  <span className={`font-mono text-4xl font-bold ${homeWinning ? 'text-green-400' : 'text-muted-foreground'}`}>
                    {(game.home_probability || 50).toFixed(0)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* User Prediction Section */}
        {game.status === 'scheduled' && (
          <div className="border-b border-border bg-purple-500/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Target className="w-6 h-6 text-purple-400" />
                  <div>
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                      Make Your Prediction
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {userPrediction 
                        ? `You picked ${userPrediction.predicted_winner === 'home' ? game.home_team?.name : userPrediction.predicted_winner === 'away' ? game.away_team?.name : (game.league_id === 'epl' || game.league_id === 'mls') ? 'DRAW' : 'TIE'}`
                        : ['nfl', 'cfl', 'epl', 'mls'].includes(game.league_id?.toLowerCase())
                          ? (game.league_id === 'epl' || game.league_id === 'mls') 
                            ? 'Pick winner or Draw (games can end level)'
                            : 'Pick winner or Tie (rare but possible)'
                          : 'Pick who will win (OT ensures a winner)'
                      }
                    </p>
                  </div>
                </div>
                
                {userPrediction ? (
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="flex items-center gap-2 py-2 px-4">
                      <CheckCircle2 className="w-4 h-4 text-green-400" />
                      Prediction Recorded
                    </Badge>
                    {userPrediction.agreed_with_platform && (
                      <Badge className="bg-purple-500/20 text-purple-400">
                        Agreed with platform
                      </Badge>
                    )}
                  </div>
                ) : isAuthenticated ? (
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      className="rounded-sm min-w-[100px]"
                      onClick={() => makePrediction('away')}
                      disabled={predictingFor !== null}
                      data-testid="predict-away-btn"
                    >
                      {predictingFor === 'away' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {game.away_team?.abbreviation} Wins
                    </Button>
                    
                    {/* TIE/DRAW button only for leagues that can end in ties */}
                    {/* NBA, NHL, and MLB use overtime to determine winners - no ties allowed */}
                    {['nfl', 'cfl', 'epl', 'mls'].includes(game.league_id?.toLowerCase()) && (
                      <Button
                        variant="outline"
                        className="rounded-sm min-w-[80px] border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10"
                        onClick={() => makePrediction('tie')}
                        disabled={predictingFor !== null}
                        data-testid="predict-tie-btn"
                      >
                        {predictingFor === 'tie' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        {(game.league_id === 'epl' || game.league_id === 'mls') ? 'DRAW' : 'TIE'}
                      </Button>
                    )}
                    
                    <Button
                      className="rounded-sm min-w-[100px]"
                      onClick={() => makePrediction('home')}
                      disabled={predictingFor !== null}
                      data-testid="predict-home-btn"
                    >
                      {predictingFor === 'home' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                      {game.home_team?.abbreviation} Wins
                    </Button>
                  </div>
                ) : (
                  <Button onClick={() => navigate('/login')} className="rounded-sm">
                    Sign In to Predict
                  </Button>
                )}
              </div>
              
              {/* Prediction Lock Notice */}
              <div className="mt-4 pt-4 border-t border-border/30">
                <div className="flex items-start gap-3 text-sm">
                  <Lock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-amber-500 font-medium">Platform Predictions Lock 10 Minutes Before Game Start</p>
                    <p className="text-muted-foreground text-xs mt-1">
                      Our platform recalculates predictions up until 10 minutes before tip-off/first pitch to incorporate the latest player attendance, lineup changes, and real-time stats for maximum accuracy.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Game Finished - Show Result */}
        {game.status === 'final' && (
          <div className={`border-b border-border ${game.prediction_correct ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-center gap-4">
                {game.prediction_correct ? (
                  <>
                    <CheckCircle2 className="w-6 h-6 text-green-400" />
                    <span className="font-display text-lg font-bold uppercase text-green-400">
                      Platform Prediction Correct
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="w-6 h-6 text-red-400" />
                    <span className="font-display text-lg font-bold uppercase text-red-400">
                      Platform Prediction Incorrect
                    </span>
                  </>
                )}
                <Badge variant="outline" className="ml-4">
                  Final Score: {game.away_score || 0} - {game.home_score || 0}
                </Badge>
              </div>
              
              {userPrediction && (
                <div className="text-center mt-3">
                  <span className="text-sm text-muted-foreground">
                    Your prediction: <span className="font-medium">
                      {userPrediction.predicted_winner === 'home' 
                        ? game.home_team?.name 
                        : userPrediction.predicted_winner === 'away' 
                          ? game.away_team?.name 
                          : (game.league_id === 'epl' || game.league_id === 'mls') ? 'DRAW' : 'TIE'}
                    </span>
                    {' '}-{' '}
                    <span className={userPrediction.is_correct ? 'text-green-400' : 'text-red-400'}>
                      {userPrediction.is_correct ? 'Correct!' : 'Incorrect'}
                    </span>
                  </span>
                </div>
              )}
              
              {(game.ai_home_score || game.platform_home_score) && (
                <div className="mt-4 text-center">
                  <button 
                    onClick={fetchPredictionDetails}
                    className="inline-flex items-center gap-4 bg-secondary/50 hover:bg-secondary/70 rounded-sm px-4 py-2 transition-colors cursor-pointer"
                  >
                    <div className="text-sm">
                      <span className="text-muted-foreground">Platform Predicted: </span>
                      <span className="font-mono font-bold">{game.ai_away_score || game.platform_away_score || 0}</span>
                      <span className="text-muted-foreground"> - </span>
                      <span className="font-mono font-bold">{game.ai_home_score || game.platform_home_score || 0}</span>
                    </div>
                    <div className="border-l border-border pl-4 text-sm">
                      <span className="text-muted-foreground">Actual: </span>
                      <span className="font-mono font-bold text-primary">{game.away_score || 0}</span>
                      <span className="text-muted-foreground"> - </span>
                      <span className="font-mono font-bold text-primary">{game.home_score || 0}</span>
                    </div>
                    <Info className="w-4 h-4 text-muted-foreground ml-2" />
                  </button>
                  <p className="text-xs text-muted-foreground mt-1">Click for detailed prediction breakdown</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Post-Game Analysis Section */}
        {game.status === 'final' && (
          <div className="border-b border-border bg-secondary/20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-purple-400" />
                Post-Game Analysis
              </h3>
              
              {loadingAnalysis ? (
                <div className="space-y-3">
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                </div>
              ) : postGameAnalysis ? (
                <div className="space-y-6">
                  {/* Headline & Summary */}
                  {postGameAnalysis.headline && (
                    <h4 className="text-xl font-bold">{postGameAnalysis.headline}</h4>
                  )}
                  {postGameAnalysis.summary && (
                    <p className="text-muted-foreground">{postGameAnalysis.summary}</p>
                  )}
                  
                  {/* Prediction Accuracy Card */}
                  {postGameAnalysis.prediction_results && (
                    <div className="bg-card border border-border rounded-sm p-4">
                      <h5 className="font-medium mb-3 text-sm uppercase tracking-wide text-purple-400 flex items-center gap-2">
                        <Target className="w-4 h-4" />
                        Platform Prediction vs Actual
                      </h5>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Probability</p>
                          <Badge 
                            className={`mt-1 ${postGameAnalysis.prediction_results.probability_correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                          >
                            {postGameAnalysis.prediction_results.probability_correct ? '✓ Correct' : '✗ Incorrect'}
                          </Badge>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Score Winner</p>
                          <Badge 
                            className={`mt-1 ${postGameAnalysis.prediction_results.score_prediction_correct ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}
                          >
                            {postGameAnalysis.prediction_results.score_prediction_correct ? '✓ Correct' : '✗ Incorrect'}
                          </Badge>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Predicted Score</p>
                          <p className="font-mono text-sm mt-1">
                            {postGameAnalysis.prediction_results.predicted_score?.away || 0} - {postGameAnalysis.prediction_results.predicted_score?.home || 0}
                          </p>
                        </div>
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Score Accuracy</p>
                          <p className="font-mono text-sm mt-1">
                            ±{postGameAnalysis.prediction_results.score_accuracy?.total_diff || 0} pts off
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* MVP Analysis */}
                  {postGameAnalysis.mvp_analysis && postGameAnalysis.mvp_analysis.player && (
                    <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-sm p-4">
                      <h5 className="font-medium mb-2 text-sm uppercase tracking-wide text-yellow-400 flex items-center gap-2">
                        <Trophy className="w-4 h-4" />
                        Game MVP
                      </h5>
                      <p className="font-bold text-lg">{postGameAnalysis.mvp_analysis.player}</p>
                      <p className="text-sm text-muted-foreground">{postGameAnalysis.mvp_analysis.team}</p>
                      {postGameAnalysis.mvp_analysis.stats && (
                        <p className="text-sm mt-1 font-mono text-primary">{postGameAnalysis.mvp_analysis.stats}</p>
                      )}
                      {postGameAnalysis.mvp_analysis.impact && (
                        <p className="text-sm text-muted-foreground mt-2">{postGameAnalysis.mvp_analysis.impact}</p>
                      )}
                    </div>
                  )}
                  
                  {/* Key Highlights */}
                  {postGameAnalysis.key_highlights && postGameAnalysis.key_highlights.length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-4">
                      <h5 className="font-medium mb-3 text-sm uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        Key Highlights
                      </h5>
                      <ul className="space-y-2">
                        {postGameAnalysis.key_highlights.map((highlight, idx) => (
                          <li key={`highlight-${idx}-${typeof highlight === 'string' ? highlight.slice(0, 20) : idx}`} className="text-sm flex items-start gap-2">
                            <span className="text-purple-400 font-bold">{idx + 1}.</span>
                            {highlight}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Turning Points */}
                    {postGameAnalysis.turning_points && postGameAnalysis.turning_points.length > 0 && (
                      <div className="bg-card border border-border rounded-sm p-4">
                        <h5 className="font-medium mb-2 text-sm uppercase tracking-wide text-muted-foreground">Turning Points</h5>
                        <ul className="space-y-1">
                          {postGameAnalysis.turning_points.map((point, idx) => (
                            <li key={`turning-${idx}-${typeof point === 'string' ? point.slice(0, 20) : idx}`} className="text-sm flex items-start gap-2">
                              <TrendingUp className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                              {point}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {/* Injuries Impact */}
                    {postGameAnalysis.injuries_impact && (
                      <div className="bg-card border border-border rounded-sm p-4">
                        <h5 className="font-medium mb-2 text-sm uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-red-400" />
                          Injuries Impact
                        </h5>
                        <p className="text-sm text-muted-foreground">{postGameAnalysis.injuries_impact}</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Prediction Breakdown */}
                  {postGameAnalysis.prediction_breakdown && (
                    <div className="bg-card border border-border rounded-sm p-4">
                      <h5 className="font-medium mb-3 text-sm uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-blue-400" />
                        Prediction Analysis
                      </h5>
                      <div className="space-y-3">
                        {postGameAnalysis.prediction_breakdown.probability_analysis && (
                          <div>
                            <p className="text-xs text-muted-foreground">Win Probability Analysis</p>
                            <p className="text-sm">{postGameAnalysis.prediction_breakdown.probability_analysis}</p>
                          </div>
                        )}
                        {postGameAnalysis.prediction_breakdown.score_analysis && (
                          <div>
                            <p className="text-xs text-muted-foreground">Score Prediction Analysis</p>
                            <p className="text-sm">{postGameAnalysis.prediction_breakdown.score_analysis}</p>
                          </div>
                        )}
                        {postGameAnalysis.prediction_breakdown.key_factors_correct?.length > 0 && (
                          <div>
                            <p className="text-xs text-green-400">What We Got Right</p>
                            <ul className="text-sm mt-1">
                              {postGameAnalysis.prediction_breakdown.key_factors_correct.map((f, i) => (
                                <li key={i} className="flex items-start gap-1"><Check className="w-3 h-3 text-green-400 mt-1" />{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {postGameAnalysis.prediction_breakdown.key_factors_missed?.length > 0 && (
                          <div>
                            <p className="text-xs text-orange-400">Areas for Improvement</p>
                            <ul className="text-sm mt-1">
                              {postGameAnalysis.prediction_breakdown.key_factors_missed.map((f, i) => (
                                <li key={i} className="flex items-start gap-1"><AlertTriangle className="w-3 h-3 text-orange-400 mt-1" />{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Box Score Key Plays */}
                  {postGameAnalysis.box_score_data?.key_plays?.length > 0 && (
                    <div className="bg-card border border-border rounded-sm p-4">
                      <h5 className="font-medium mb-3 text-sm uppercase tracking-wide text-muted-foreground flex items-center gap-2">
                        <Clock className="w-4 h-4 text-cyan-400" />
                        Key Scoring Plays
                      </h5>
                      <div className="space-y-2">
                        {postGameAnalysis.box_score_data.key_plays.map((play, idx) => (
                          <div key={`play-${idx}-${play?.period || 'unknown'}-${play?.time || idx}`} className="text-sm border-l-2 border-cyan-400/30 pl-3">
                            <span className="text-cyan-400 font-mono text-xs">{play.period} {play.time}</span>
                            <p className="text-muted-foreground">{play.description}</p>
                            <p className="font-mono text-xs mt-1">{play.away_score} - {play.home_score}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Future Insights */}
                  {postGameAnalysis.future_prediction_insights && (
                    <div className="bg-blue-500/10 border border-blue-500/20 rounded-sm p-4">
                      <h5 className="font-medium mb-3 text-sm uppercase tracking-wide text-blue-400 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Insights for Future Predictions
                      </h5>
                      {postGameAnalysis.future_prediction_insights.model_adjustments && (
                        <p className="text-sm">{postGameAnalysis.future_prediction_insights.model_adjustments}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Post-game analysis will be available shortly.</p>
              )}
            </div>
          </div>
        )}

        {/* Live Game Status */}
        {game.status === 'live' && (
          <div className="border-b border-border bg-red-500/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex flex-col items-center gap-4">
                <div className="flex items-center gap-4">
                  <Radio className="w-6 h-6 text-red-400 animate-pulse" />
                  <span className="font-display text-xl font-bold uppercase text-red-400">Live</span>
                  {(liveScore?.status_detail || game.status_detail) && (
                    <Badge variant="outline" className="text-red-400 border-red-400/30">
                      {liveScore?.status_detail || game.status_detail}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-8">
                  <div className="text-center">
                    <span className="font-mono text-4xl font-bold text-foreground">
                      {liveScore?.away_score ?? game.away_score ?? 0}
                    </span>
                    <p className="text-sm text-muted-foreground mt-1">{game.away_team?.abbreviation}</p>
                  </div>
                  <span className="text-2xl text-muted-foreground">-</span>
                  <div className="text-center">
                    <span className="font-mono text-4xl font-bold text-foreground">
                      {liveScore?.home_score ?? game.home_score ?? 0}
                    </span>
                    <p className="text-sm text-muted-foreground mt-1">{game.home_team?.abbreviation}</p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Updates every 30 seconds</p>
              </div>
              
              {(game.ai_predicted_score || game.ai_home_score) && (
                <button 
                  onClick={fetchPredictionDetails}
                  className="mt-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-sm max-w-md mx-auto w-full hover:bg-purple-500/20 transition-colors cursor-pointer"
                >
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-purple-400" />
                    <span className="text-xs font-medium text-purple-400 uppercase tracking-wide">Platform Pre-Game Prediction</span>
                    <Info className="w-3 h-3 text-purple-400" />
                  </div>
                  <div className="flex items-center justify-center gap-4">
                    <span className="font-mono text-lg font-bold">{game.ai_away_score || 0}</span>
                    <span className="text-xs text-muted-foreground">{game.away_team?.abbreviation}</span>
                    <span className="text-muted-foreground">-</span>
                    <span className="font-mono text-lg font-bold">{game.ai_home_score || 0}</span>
                    <span className="text-xs text-muted-foreground">{game.home_team?.abbreviation}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">Click for full analysis</p>
                </button>
              )}
            </div>
          </div>
        )}

        {/* Content Tabs - 7 Tab Structure */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main content */}
            <div className="lg:col-span-2">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="w-full justify-start rounded-sm overflow-x-auto flex-nowrap">
                  <TabsTrigger value="overview" className="rounded-sm" data-testid="tab-overview">
                    <Activity className="w-4 h-4 mr-2" />
                    Overview
                  </TabsTrigger>
                  <TabsTrigger value="trends" className="rounded-sm" data-testid="tab-trends">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Trends
                  </TabsTrigger>
                  <TabsTrigger value="prediction" className="rounded-sm" data-testid="tab-prediction">
                    <Brain className="w-4 h-4 mr-2" />
                    Prediction
                  </TabsTrigger>
                  <TabsTrigger value="h2h" className="rounded-sm" data-testid="tab-h2h">
                    <Users className="w-4 h-4 mr-2" />
                    H2H
                  </TabsTrigger>
                  {game.league_id?.toLowerCase() === 'mlb' && (
                    <TabsTrigger value="pitchers" className="rounded-sm" data-testid="tab-pitchers">
                      <Zap className="w-4 h-4 mr-2" />
                      Pitchers
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="injuries" className="rounded-sm" data-testid="tab-injuries">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Injuries
                  </TabsTrigger>
                  <TabsTrigger value="schedule" className="rounded-sm" data-testid="tab-schedule">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule
                  </TabsTrigger>
                  <TabsTrigger value="confidence" className="rounded-sm" data-testid="tab-confidence">
                    <Gauge className="w-4 h-4 mr-2" />
                    Confidence
                  </TabsTrigger>
                </TabsList>

                {/* Overview Tab */}
                <TabsContent value="overview" className="mt-6">
                  {/* Learning Insights Panel - Shows what AI learned from past games */}
                  {game.status !== 'final' && (
                    <div className="mb-6">
                      <LearningInsightsPanel gameId={gameId} />
                    </div>
                  )}
                  
                  {/* AI Learning Insights - Platform Learning Stats */}
                  {aiLearningInsights && aiLearningInsights.platform_learning_active && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-sm p-6 mb-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-purple-500/20 rounded-lg">
                          <Brain className="w-5 h-5 text-purple-400" />
                        </div>
                        <div>
                          <h3 className="font-display text-lg font-bold uppercase tracking-tight">AI Learning Insights</h3>
                          <p className="text-xs text-muted-foreground">
                            Platform continuously learns from {aiLearningInsights.total_predictions_analyzed || 0} analyzed predictions
                          </p>
                        </div>
                        {aiLearningInsights.league_accuracy && (
                          <Badge variant="outline" className="ml-auto bg-purple-500/20 text-purple-400">
                            {aiLearningInsights.league_accuracy}% {game.league_id?.toUpperCase()} Accuracy
                          </Badge>
                        )}
                      </div>
                      
                      {/* Team Insights Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {/* Home Team AI Insights */}
                        {aiLearningInsights.home_team_insights && (
                          <div className="bg-card/50 border border-border/50 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <img 
                                src={game.home_team?.logo} 
                                alt="" 
                                className="w-6 h-6 object-contain"
                              />
                              <span className="font-medium text-sm">{game.home_team?.name} (Home)</span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Games Analyzed</span>
                                <span className="font-mono">{aiLearningInsights.home_team_insights.games_analyzed}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Overall Win Rate</span>
                                <span className={`font-mono ${aiLearningInsights.home_team_insights.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                                  {aiLearningInsights.home_team_insights.win_rate}%
                                </span>
                              </div>
                              {aiLearningInsights.home_team_insights.home_win_rate !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Home Win Rate</span>
                                  <span className={`font-mono ${aiLearningInsights.home_team_insights.home_win_rate >= 50 ? 'text-green-400' : 'text-yellow-400'}`}>
                                    {aiLearningInsights.home_team_insights.home_win_rate}%
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Prediction Accuracy</span>
                                <span className={`font-mono ${aiLearningInsights.home_team_insights.prediction_accuracy >= 50 ? 'text-purple-400' : 'text-orange-400'}`}>
                                  {aiLearningInsights.home_team_insights.prediction_accuracy}%
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Away Team AI Insights */}
                        {aiLearningInsights.away_team_insights && (
                          <div className="bg-card/50 border border-border/50 rounded-lg p-4">
                            <div className="flex items-center gap-2 mb-3">
                              <img 
                                src={game.away_team?.logo} 
                                alt="" 
                                className="w-6 h-6 object-contain"
                              />
                              <span className="font-medium text-sm">{game.away_team?.name} (Away)</span>
                            </div>
                            <div className="space-y-2 text-sm">
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Games Analyzed</span>
                                <span className="font-mono">{aiLearningInsights.away_team_insights.games_analyzed}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Overall Win Rate</span>
                                <span className={`font-mono ${aiLearningInsights.away_team_insights.win_rate >= 50 ? 'text-green-400' : 'text-red-400'}`}>
                                  {aiLearningInsights.away_team_insights.win_rate}%
                                </span>
                              </div>
                              {aiLearningInsights.away_team_insights.away_win_rate !== null && (
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Road Win Rate</span>
                                  <span className={`font-mono ${aiLearningInsights.away_team_insights.away_win_rate >= 40 ? 'text-green-400' : 'text-yellow-400'}`}>
                                    {aiLearningInsights.away_team_insights.away_win_rate}%
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-muted-foreground">Prediction Accuracy</span>
                                <span className={`font-mono ${aiLearningInsights.away_team_insights.prediction_accuracy >= 50 ? 'text-purple-400' : 'text-orange-400'}`}>
                                  {aiLearningInsights.away_team_insights.prediction_accuracy}%
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Matchup History */}
                      {aiLearningInsights.matchup_history && (
                        <div className="bg-card/30 border border-border/30 rounded-lg p-3 mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <History className="w-4 h-4 text-blue-400" />
                              <span className="text-sm font-medium">Matchup History</span>
                            </div>
                            <div className="text-sm">
                              <span className="text-muted-foreground">Platform is </span>
                              <span className={`font-bold ${aiLearningInsights.matchup_history.accuracy >= 60 ? 'text-green-400' : aiLearningInsights.matchup_history.accuracy >= 40 ? 'text-yellow-400' : 'text-red-400'}`}>
                                {aiLearningInsights.matchup_history.accuracy}% accurate
                              </span>
                              <span className="text-muted-foreground"> on this matchup</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                ({aiLearningInsights.matchup_history.correct_predictions}/{aiLearningInsights.matchup_history.previous_predictions} correct)
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Confidence Reasoning */}
                      {aiLearningInsights.confidence_reasoning && aiLearningInsights.confidence_reasoning.length > 0 && (
                        <div className="border-t border-border/30 pt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Lightbulb className="w-4 h-4 text-yellow-400" />
                            <span className="text-sm font-medium">Why This Prediction?</span>
                          </div>
                          <ul className="space-y-1">
                            {aiLearningInsights.confidence_reasoning.map((reason, idx) => (
                              <li key={`reason-${idx}-${typeof reason === 'string' ? reason.slice(0, 20) : idx}`} className="text-sm text-muted-foreground flex items-start gap-2">
                                <Check className="w-3 h-3 text-green-400 mt-1 flex-shrink-0" />
                                {reason}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Factor Effectiveness */}
                      {aiLearningInsights.factor_effectiveness && aiLearningInsights.factor_effectiveness.length > 0 && (
                        <div className="border-t border-border/30 pt-4 mt-4">
                          <div className="flex items-center gap-2 mb-3">
                            <BarChart3 className="w-4 h-4 text-cyan-400" />
                            <span className="text-sm font-medium">Prediction Factor Effectiveness</span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {aiLearningInsights.factor_effectiveness.slice(0, 4).map((factor) => (
                              <Badge 
                                key={`factor-${factor.factor}`} 
                                variant="outline" 
                                className={`text-xs ${
                                  factor.effectiveness === 'high' ? 'bg-green-500/10 text-green-400 border-green-500/30' :
                                  factor.effectiveness === 'medium' ? 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' :
                                  'bg-red-500/10 text-red-400 border-red-500/30'
                                }`}
                              >
                                {factor.factor?.replace(/_/g, ' ')}: {(factor.accuracy * 100).toFixed(0)}%
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="bg-card border border-border rounded-sm p-6">
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                      Why This Probability?
                    </h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      Top factors influencing our prediction model:
                    </p>
                    <div className="space-y-4">
                      {drivers && drivers.length > 0 ? drivers.map((driver, idx) => (
                        <div key={`driver-${idx}-${typeof driver === 'string' ? driver.slice(0, 20) : (driver?.factor || idx)}`} className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{typeof driver === 'string' ? driver : (driver.factor || 'Factor')}</p>
                            <p className="text-xs text-muted-foreground">{typeof driver === 'string' ? '' : (driver.description || '')}</p>
                          </div>
                          {typeof driver === 'object' && driver.impact !== undefined && typeof driver.impact === 'number' && (
                            <div className="flex items-center gap-2">
                              {driver.impact > 0 ? (
                                <TrendingUp className="w-4 h-4 text-green-400" />
                              ) : (
                                <TrendingDown className="w-4 h-4 text-red-400" />
                              )}
                              <span className={`font-mono text-sm ${driver.impact > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {driver.impact > 0 ? '+' : ''}{Number(driver.impact).toFixed(1)}
                              </span>
                            </div>
                          )}
                        </div>
                      )) : (
                        <p className="text-muted-foreground">No driver data available</p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Trends Tab */}
                <TabsContent value="trends" className="mt-6">
                  <div className="space-y-6">
                    {loadingTrends ? (
                      <div className="space-y-4">
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                      </div>
                    ) : trendsData ? (
                      <>
                        {/* Team Performance Trends */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Home Team Trends */}
                          <div className="bg-card border border-border rounded-sm p-4">
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-green-400" />
                              {trendsData.home_team} Trends
                            </h4>
                            {trendsData.home_trends?.record ? (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Overall Record</span>
                                  <span className="font-mono font-medium">{trendsData.home_trends.record}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Home Record</span>
                                  <span className="font-mono">{trendsData.home_trends.home_record}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Away Record</span>
                                  <span className="font-mono">{trendsData.home_trends.away_record}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Streak</span>
                                  <span className={`font-mono ${trendsData.home_trends.streak?.startsWith('W') ? 'text-green-400' : 'text-red-400'}`}>
                                    {trendsData.home_trends.streak}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">PPG</span>
                                  <span className="font-mono">{trendsData.home_trends.points_per_game}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Opp PPG</span>
                                  <span className="font-mono">{trendsData.home_trends.points_against}</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No trend data available</p>
                            )}
                          </div>

                          {/* Away Team Trends */}
                          <div className="bg-card border border-border rounded-sm p-4">
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-blue-400" />
                              {trendsData.away_team} Trends
                            </h4>
                            {trendsData.away_trends?.record ? (
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Overall Record</span>
                                  <span className="font-mono font-medium">{trendsData.away_trends.record}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Home Record</span>
                                  <span className="font-mono">{trendsData.away_trends.home_record}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Away Record</span>
                                  <span className="font-mono">{trendsData.away_trends.away_record}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Streak</span>
                                  <span className={`font-mono ${trendsData.away_trends.streak?.startsWith('W') ? 'text-green-400' : 'text-red-400'}`}>
                                    {trendsData.away_trends.streak}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">PPG</span>
                                  <span className="font-mono">{trendsData.away_trends.points_per_game}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Opp PPG</span>
                                  <span className="font-mono">{trendsData.away_trends.points_against}</span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-sm text-muted-foreground">No trend data available</p>
                            )}
                          </div>
                        </div>

                        {/* Betting Trends */}
                        <div className="bg-card border border-border rounded-sm p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <BarChart3 className="w-4 h-4 text-purple-400" />
                            Betting Insights
                          </h4>
                          {trendsData.betting_trends && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div className="text-center p-3 bg-secondary/30 rounded-sm">
                                <p className="text-muted-foreground text-xs uppercase">Favorite</p>
                                <p className="font-medium">{trendsData.betting_trends.favorite}</p>
                                <p className="text-green-400 font-mono">{trendsData.betting_trends.favorite_probability}%</p>
                              </div>
                              <div className="text-center p-3 bg-secondary/30 rounded-sm">
                                <p className="text-muted-foreground text-xs uppercase">Underdog</p>
                                <p className="font-medium">{trendsData.betting_trends.underdog}</p>
                                <p className="text-yellow-400 font-mono">{trendsData.betting_trends.underdog_probability}%</p>
                              </div>
                              <div className="text-center p-3 bg-secondary/30 rounded-sm">
                                <p className="text-muted-foreground text-xs uppercase">Est. Spread</p>
                                <p className="font-mono font-medium">{trendsData.betting_trends.spread_estimate?.toFixed(1)}</p>
                              </div>
                              <div className="text-center p-3 bg-secondary/30 rounded-sm">
                                <p className="text-muted-foreground text-xs uppercase">Pick 'Em?</p>
                                <p className="font-medium">{trendsData.betting_trends.is_pick_em ? 'Yes' : 'No'}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="text-muted-foreground">No trends data available</p>
                    )}
                  </div>
                </TabsContent>

                {/* Platform Prediction Tab */}
                <TabsContent value="prediction" className="mt-6">
                  <div className="space-y-6">
                    {/* Platform Score Prediction */}
                    <div className="bg-purple-500/10 border border-purple-500/20 rounded-sm p-6">
                      <h4 className="font-medium mb-4 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-purple-400" />
                        Platform Score Prediction
                      </h4>
                      
                      {(game.ai_home_score || game.platform_home_score) ? (
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-8 mb-4">
                            <div className="text-center">
                              <p className="font-mono text-4xl font-bold">{game.ai_away_score || game.platform_away_score || 0}</p>
                              <p className="text-sm text-muted-foreground mt-1">{game.away_team?.name}</p>
                            </div>
                            <span className="text-2xl text-muted-foreground">-</span>
                            <div className="text-center">
                              <p className="font-mono text-4xl font-bold">{game.ai_home_score || game.platform_home_score || 0}</p>
                              <p className="text-sm text-muted-foreground mt-1">{game.home_team?.name}</p>
                            </div>
                          </div>
                          
                          {game.ai_analysis_summary && (
                            <p className="text-sm text-muted-foreground mt-4 max-w-xl mx-auto">
                              {game.ai_analysis_summary}
                            </p>
                          )}
                          
                          <Button 
                            variant="outline" 
                            className="mt-4 rounded-sm"
                            onClick={fetchPredictionDetails}
                          >
                            <Info className="w-4 h-4 mr-2" />
                            View Full Analysis
                          </Button>
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-muted-foreground mb-4">Platform prediction not yet generated</p>
                          <Badge variant="outline">Win Probability: {homeWinning ? game.home_team?.name : game.away_team?.name} {Math.max(game.home_probability, game.away_probability)}%</Badge>
                        </div>
                      )}
                    </div>

                    {/* Win Probability Breakdown */}
                    <div className="bg-card border border-border rounded-sm p-6">
                      <h4 className="font-medium mb-4 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-blue-400" />
                        Win Probability
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span>{game.home_team?.name} (Home)</span>
                            <span className="font-mono">{game.home_probability}%</span>
                          </div>
                          <Progress value={game.home_probability} className="h-3" />
                        </div>
                        <div>
                          <div className="flex justify-between text-sm mb-2">
                            <span>{game.away_team?.name} (Away)</span>
                            <span className="font-mono">{game.away_probability}%</span>
                          </div>
                          <Progress value={game.away_probability} className="h-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Head-to-Head Tab */}
                <TabsContent value="h2h" className="mt-6">
                  <div className="space-y-6">
                    {/* H2H Record */}
                    <div className="bg-card border border-border rounded-sm p-6">
                      <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
                        Head-to-Head Record
                      </h3>
                      {trendsData?.h2h_trends && trendsData.h2h_trends.total_games > 0 ? (
                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <div className="text-center">
                              <p className="text-3xl font-bold font-mono text-green-400">
                                {trendsData.h2h_trends.home_wins}
                              </p>
                              <p className="text-sm text-muted-foreground">{game.home_team?.name}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-muted-foreground">Last {trendsData.h2h_trends.total_games} Games</p>
                            </div>
                            <div className="text-center">
                              <p className="text-3xl font-bold font-mono text-blue-400">
                                {trendsData.h2h_trends.away_wins}
                              </p>
                              <p className="text-sm text-muted-foreground">{game.away_team?.name}</p>
                            </div>
                          </div>
                          
                          {trendsData.h2h_trends.last_5_results?.length > 0 && (
                            <div>
                              <p className="text-sm text-muted-foreground mb-2">Recent Meetings</p>
                              <div className="flex gap-2">
                                {trendsData.h2h_trends.last_5_results.map((result, idx) => (
                                  <div 
                                    key={`h2h-result-${idx}-${result}`}
                                    className={`w-10 h-10 rounded-sm flex items-center justify-center font-bold ${
                                      result === 'H' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'
                                    }`}
                                  >
                                    {result}
                                  </div>
                                ))}
                              </div>
                              <p className="text-xs text-muted-foreground mt-2">H = Home team win, A = Away team win</p>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="p-3 bg-secondary/30 rounded-sm text-center">
                              <p className="text-muted-foreground text-xs uppercase">Avg Home Score</p>
                              <p className="font-mono font-bold text-lg">{trendsData.h2h_trends.avg_home_score}</p>
                            </div>
                            <div className="p-3 bg-secondary/30 rounded-sm text-center">
                              <p className="text-muted-foreground text-xs uppercase">Avg Away Score</p>
                              <p className="font-mono font-bold text-lg">{trendsData.h2h_trends.avg_away_score}</p>
                            </div>
                          </div>
                        </div>
                      ) : head_to_head && !head_to_head.locked ? (
                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <div className="text-center">
                              <p className="text-3xl font-bold font-mono text-blue-400">{head_to_head.away_wins || 0}</p>
                              <p className="text-sm text-muted-foreground">{game.away_team?.name}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-muted-foreground">Season Series</p>
                            </div>
                            <div className="text-center">
                              <p className="text-3xl font-bold font-mono text-green-400">{head_to_head.home_wins || 0}</p>
                              <p className="text-sm text-muted-foreground">{game.home_team?.name}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No head-to-head data available</p>
                      )}
                    </div>

                    {/* Player Stats - Now shows game-specific players with statistics */}
                    <div className="bg-card border border-border rounded-sm p-6" data-testid="h2h-player-stats">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                          Key Players
                          <span className="text-xs font-normal text-muted-foreground ml-2">(sorted by highest stats)</span>
                        </h3>
                        {gamePlayers?.stats_type && (
                          <Badge variant="outline" className={
                            gamePlayers.stats_type === 'game' ? 'text-green-400 border-green-400/30' :
                            gamePlayers.stats_type === 'final' ? 'text-blue-400 border-blue-400/30' :
                            'text-muted-foreground border-muted-foreground/30'
                          }>
                            {gamePlayers.stats_type === 'game' ? 'Live Stats' : 
                             gamePlayers.stats_type === 'final' ? 'Game Stats' : 
                             'Season Averages'}
                          </Badge>
                        )}
                      </div>
                      {loadingStats ? (
                        <div className="space-y-3">
                          <Skeleton className="h-16 w-full" />
                          <Skeleton className="h-16 w-full" />
                        </div>
                      ) : gamePlayers ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          {/* Away Team Players - Sorted by primary stat (PTS for NBA, G for NHL, etc.) */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              {game.away_team?.logo_url && (
                                <img src={game.away_team.logo_url} alt={game.away_team.name} className="w-6 h-6 object-contain" />
                              )}
                              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                {game.away_team?.name}
                              </p>
                            </div>
                            <div className="space-y-2">
                              {/* Sort players by highest points/goals/primary stat */}
                              {[...(gamePlayers.away_team?.players || [])]
                                .sort((a, b) => {
                                  const getPrimaryStat = (player) => {
                                    const stats = player.statistics || {};
                                    // Try PTS first (NBA), then G (NHL/MLS), then other metrics
                                    return parseFloat(stats.PTS || stats.G || stats.HR || stats.YDS || stats.AVG || 0);
                                  };
                                  return getPrimaryStat(b) - getPrimaryStat(a);
                                })
                                .slice(0, 12).map((player, idx) => (
                                <div 
                                  key={player.player_id || `away-${player.name}`} 
                                  onClick={() => fetchPlayerPrediction(player, 'away')}
                                  className={`p-3 rounded-sm cursor-pointer transition-all hover:scale-[1.02] hover:shadow-md ${player.is_injured ? 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/20' : 'bg-secondary/30 hover:bg-secondary/50'}`}
                                  data-testid={`player-card-away-${player.player_id || player.name}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="relative">
                                      {player.headshot ? (
                                        <img 
                                          src={player.headshot} 
                                          alt={player.name}
                                          className={`w-10 h-10 rounded-full object-cover ${player.is_injured ? 'opacity-60' : ''}`}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <div className={`w-10 h-10 bg-secondary rounded-full flex items-center justify-center ${player.is_injured ? 'opacity-60' : ''}`}>
                                          <Users className="w-5 h-5 text-muted-foreground" />
                                        </div>
                                      )}
                                      {player.is_injured && (
                                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                          <span className="text-[10px] text-white font-bold">!</span>
                                        </div>
                                      )}
                                      {/* Rank badge for top performers */}
                                      {idx < 3 && (
                                        <div className={`absolute -bottom-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                          idx === 0 ? 'bg-yellow-500 text-yellow-900' :
                                          idx === 1 ? 'bg-gray-300 text-gray-700' :
                                          'bg-amber-700 text-amber-100'
                                        }`}>
                                          {idx + 1}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className={`text-sm font-medium ${player.is_injured ? 'text-red-400' : 'text-primary hover:underline'}`}>{player.name}</p>
                                        {player.is_injured && (
                                          <Badge variant="outline" className="text-[10px] py-0 px-1 text-red-400 border-red-400/30">
                                            {player.injury_status === 'day-to-day' ? 'DTD' : 
                                             player.injury_status === 'out' ? 'OUT' : 
                                             player.injury_status?.toUpperCase().slice(0, 3) || 'INJ'}
                                          </Badge>
                                        )}
                                        <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-180 ml-auto" />
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {player.position} {player.jersey && `#${player.jersey}`}
                                        {player.injury_detail && <span className="ml-1 text-red-400">({player.injury_detail})</span>}
                                      </p>
                                    </div>
                                  </div>
                                  {/* Player Statistics */}
                                  {player.statistics && Object.keys(player.statistics).length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-border/50">
                                      <div className="flex flex-wrap gap-2">
                                        {Object.entries(player.statistics).slice(0, 5).map(([key, value], i) => (
                                          <div key={i} className="text-center px-2 py-1 bg-secondary/50 rounded">
                                            <p className="text-xs text-muted-foreground">{key}</p>
                                            <p className="text-sm font-mono font-medium">{value}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {(!gamePlayers.away_team?.players || gamePlayers.away_team.players.length === 0) && (
                                <p className="text-sm text-muted-foreground">No player data available</p>
                              )}
                            </div>
                          </div>
                          
                          {/* Home Team Players - Sorted by primary stat (PTS for NBA, G for NHL, etc.) */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              {game.home_team?.logo_url && (
                                <img src={game.home_team.logo_url} alt={game.home_team.name} className="w-6 h-6 object-contain" />
                              )}
                              <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                                {game.home_team?.name}
                              </p>
                            </div>
                            <div className="space-y-2">
                              {/* Sort players by highest points/goals/primary stat */}
                              {[...(gamePlayers.home_team?.players || [])]
                                .sort((a, b) => {
                                  const getPrimaryStat = (player) => {
                                    const stats = player.statistics || {};
                                    // Try PTS first (NBA), then G (NHL/MLS), then other metrics
                                    return parseFloat(stats.PTS || stats.G || stats.HR || stats.YDS || stats.AVG || 0);
                                  };
                                  return getPrimaryStat(b) - getPrimaryStat(a);
                                })
                                .slice(0, 12).map((player, idx) => (
                                <div 
                                  key={player.player_id || `home-${player.name}`} 
                                  onClick={() => fetchPlayerPrediction(player, 'home')}
                                  className={`p-3 rounded-sm cursor-pointer transition-all hover:scale-[1.02] hover:shadow-md ${player.is_injured ? 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/20' : 'bg-secondary/30 hover:bg-secondary/50'}`}
                                  data-testid={`player-card-home-${player.player_id || player.name}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="relative">
                                      {player.headshot ? (
                                        <img 
                                          src={player.headshot} 
                                          alt={player.name}
                                          className={`w-10 h-10 rounded-full object-cover ${player.is_injured ? 'opacity-60' : ''}`}
                                          onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                      ) : (
                                        <div className={`w-10 h-10 bg-secondary rounded-full flex items-center justify-center ${player.is_injured ? 'opacity-60' : ''}`}>
                                          <Users className="w-5 h-5 text-muted-foreground" />
                                        </div>
                                      )}
                                      {player.is_injured && (
                                        <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                                          <span className="text-[10px] text-white font-bold">!</span>
                                        </div>
                                      )}
                                      {/* Rank badge for top performers */}
                                      {idx < 3 && (
                                        <div className={`absolute -bottom-1 -left-1 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                          idx === 0 ? 'bg-yellow-500 text-yellow-900' :
                                          idx === 1 ? 'bg-gray-300 text-gray-700' :
                                          'bg-amber-700 text-amber-100'
                                        }`}>
                                          {idx + 1}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <p className={`text-sm font-medium ${player.is_injured ? 'text-red-400' : 'text-primary hover:underline'}`}>{player.name}</p>
                                        {player.is_injured && (
                                          <Badge variant="outline" className="text-[10px] py-0 px-1 text-red-400 border-red-400/30">
                                            {player.injury_status === 'day-to-day' ? 'DTD' : 
                                             player.injury_status === 'out' ? 'OUT' : 
                                             player.injury_status?.toUpperCase().slice(0, 3) || 'INJ'}
                                          </Badge>
                                        )}
                                        <ChevronLeft className="w-4 h-4 text-muted-foreground rotate-180 ml-auto" />
                                      </div>
                                      <p className="text-xs text-muted-foreground">
                                        {player.position} {player.jersey && `#${player.jersey}`}
                                        {player.injury_detail && <span className="ml-1 text-red-400">({player.injury_detail})</span>}
                                      </p>
                                    </div>
                                  </div>
                                  {/* Player Statistics */}
                                  {player.statistics && Object.keys(player.statistics).length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-border/50">
                                      <div className="flex flex-wrap gap-2">
                                        {Object.entries(player.statistics).slice(0, 5).map(([key, value], i) => (
                                          <div key={i} className="text-center px-2 py-1 bg-secondary/50 rounded">
                                            <p className="text-xs text-muted-foreground">{key}</p>
                                            <p className="text-sm font-mono font-medium">{value}</p>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {(!gamePlayers.home_team?.players || gamePlayers.home_team.players.length === 0) && (
                                <p className="text-sm text-muted-foreground">No player data available</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted-foreground">No player data available for this game</p>
                      )}
                    </div>

                    {/* Player Performance Matchups */}
                    <PlayerPerformanceCard gameId={gameId} leagueId={game.league_id} />
                  </div>
                </TabsContent>

                {/* Pitchers Tab (MLB only) */}
                {game.league_id?.toLowerCase() === 'mlb' && (
                  <TabsContent value="pitchers" className="mt-6">
                    <PitcherMatchupCard gameId={gameId} />
                  </TabsContent>
                )}

                {/* Injuries Tab */}
                <TabsContent value="injuries" className="mt-6">
                  <div className="space-y-6">
                    {/* Home Team Injuries */}
                    <div className="bg-card border border-border rounded-sm p-6">
                      <div className="flex items-center gap-3 mb-4">
                        {game.home_team?.logo_url && (
                          <img src={game.home_team.logo_url} alt={game.home_team.name} className="w-8 h-8 object-contain" />
                        )}
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                          {game.home_team?.name} Injuries
                        </h3>
                        <Badge variant="outline" className="text-muted-foreground">
                          {teamInjuries.home.length} Players
                        </Badge>
                      </div>
                      {loadingInjuries ? (
                        <div className="space-y-2">
                          <Skeleton className="h-12 w-full" />
                          <Skeleton className="h-12 w-full" />
                        </div>
                      ) : teamInjuries.home.length > 0 ? (
                        <div className="space-y-2">
                          {teamInjuries.home.map((injury) => (
                            <div key={injury.player_id || `${injury.team_id}-${injury.player_name}`} className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                              <div>
                                <p className="font-medium">{injury.player_name || 'Unknown Player'}</p>
                                <p className="text-xs text-muted-foreground">
                                  {injury.position && `${injury.position} - `}
                                  {injury.type || injury.comment || 'Injury details unavailable'}
                                </p>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={
                                  injury.status === 'Out' ? 'text-red-400 border-red-400/30' :
                                  injury.status === 'Doubtful' ? 'text-orange-400 border-orange-400/30' :
                                  injury.status === 'Questionable' ? 'text-yellow-400 border-yellow-400/30' :
                                  'text-green-400 border-green-400/30'
                                }
                              >
                                {injury.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No injuries reported</p>
                      )}
                    </div>

                    {/* Away Team Injuries */}
                    <div className="bg-card border border-border rounded-sm p-6">
                      <div className="flex items-center gap-3 mb-4">
                        {game.away_team?.logo_url && (
                          <img src={game.away_team.logo_url} alt={game.away_team.name} className="w-8 h-8 object-contain" />
                        )}
                        <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                          {game.away_team?.name} Injuries
                        </h3>
                        <Badge variant="outline" className="text-muted-foreground">
                          {teamInjuries.away.length} Players
                        </Badge>
                      </div>
                      {loadingInjuries ? (
                        <div className="space-y-2">
                          <Skeleton className="h-12 w-full" />
                          <Skeleton className="h-12 w-full" />
                        </div>
                      ) : teamInjuries.away.length > 0 ? (
                        <div className="space-y-2">
                          {teamInjuries.away.map((injury) => (
                            <div key={injury.player_id || `${injury.team_id}-${injury.player_name}`} className="flex items-center justify-between p-3 bg-secondary/50 rounded-sm">
                              <div>
                                <p className="font-medium">{injury.player_name || 'Unknown Player'}</p>
                                <p className="text-xs text-muted-foreground">
                                  {injury.position && `${injury.position} - `}
                                  {injury.type || injury.comment || 'Injury details unavailable'}
                                </p>
                              </div>
                              <Badge 
                                variant="outline" 
                                className={
                                  injury.status === 'Out' ? 'text-red-400 border-red-400/30' :
                                  injury.status === 'Doubtful' ? 'text-orange-400 border-orange-400/30' :
                                  injury.status === 'Questionable' ? 'text-yellow-400 border-yellow-400/30' :
                                  'text-green-400 border-green-400/30'
                                }
                              >
                                {injury.status}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm">No injuries reported</p>
                      )}
                    </div>
                  </div>
                </TabsContent>

                {/* Schedule & Fatigue Tab */}
                <TabsContent value="schedule" className="mt-6">
                  <div className="space-y-6">
                    {loadingSchedule ? (
                      <div className="space-y-4">
                        <Skeleton className="h-32 w-full" />
                        <Skeleton className="h-32 w-full" />
                      </div>
                    ) : scheduleData ? (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Home Team Schedule */}
                          <div className="bg-card border border-border rounded-sm p-4">
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-green-400" />
                              {scheduleData.home_team} Schedule
                            </h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Days Rest</span>
                                <span className={`font-mono font-medium ${
                                  scheduleData.home_schedule?.days_rest === 'N/A' ? 'text-muted-foreground' :
                                  scheduleData.home_schedule?.days_rest <= 1 ? 'text-red-400' :
                                  scheduleData.home_schedule?.days_rest <= 2 ? 'text-yellow-400' : 'text-green-400'
                                }`}>
                                  {scheduleData.home_schedule?.days_rest}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Games in Last 7 Days</span>
                                <span className="font-mono">{scheduleData.home_schedule?.games_in_7_days}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Back-to-Back</span>
                                <Badge variant="outline" className={scheduleData.home_schedule?.back_to_back ? 'text-red-400' : 'text-green-400'}>
                                  {scheduleData.home_schedule?.back_to_back ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Fatigue Score</span>
                                <div className="flex items-center gap-2">
                                  <Progress value={scheduleData.home_schedule?.fatigue_score || 0} className="w-20 h-2" />
                                  <span className="font-mono text-xs">{scheduleData.home_schedule?.fatigue_score}%</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Away Team Schedule */}
                          <div className="bg-card border border-border rounded-sm p-4">
                            <h4 className="font-medium mb-3 flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-blue-400" />
                              {scheduleData.away_team} Schedule
                            </h4>
                            <div className="space-y-3 text-sm">
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Days Rest</span>
                                <span className={`font-mono font-medium ${
                                  scheduleData.away_schedule?.days_rest === 'N/A' ? 'text-muted-foreground' :
                                  scheduleData.away_schedule?.days_rest <= 1 ? 'text-red-400' :
                                  scheduleData.away_schedule?.days_rest <= 2 ? 'text-yellow-400' : 'text-green-400'
                                }`}>
                                  {scheduleData.away_schedule?.days_rest}
                                </span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Games in Last 7 Days</span>
                                <span className="font-mono">{scheduleData.away_schedule?.games_in_7_days}</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Back-to-Back</span>
                                <Badge variant="outline" className={scheduleData.away_schedule?.back_to_back ? 'text-red-400' : 'text-green-400'}>
                                  {scheduleData.away_schedule?.back_to_back ? 'Yes' : 'No'}
                                </Badge>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="text-muted-foreground">Fatigue Score</span>
                                <div className="flex items-center gap-2">
                                  <Progress value={scheduleData.away_schedule?.fatigue_score || 0} className="w-20 h-2" />
                                  <span className="font-mono text-xs">{scheduleData.away_schedule?.fatigue_score}%</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Rest Advantage */}
                        <div className="bg-purple-500/10 border border-purple-500/20 rounded-sm p-4 text-center">
                          <h4 className="font-medium mb-2">Rest Advantage</h4>
                          <p className="text-lg font-bold text-purple-400">
                            {scheduleData.rest_advantage === 'Even' ? 'Even - No advantage' : `${scheduleData.rest_advantage} has the rest advantage`}
                          </p>
                        </div>

                        {/* Venue Info */}
                        {scheduleData.venue && (
                          <div className="bg-card border border-border rounded-sm p-4">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">Venue:</span>
                              <span className="font-medium">{scheduleData.venue}</span>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-muted-foreground">No schedule data available</p>
                    )}
                  </div>
                </TabsContent>

                {/* Confidence Score Tab */}
                <TabsContent value="confidence" className="mt-6">
                  <div className="space-y-6">
                    {loadingConfidence ? (
                      <div className="space-y-4">
                        <Skeleton className="h-40 w-full" />
                        <Skeleton className="h-32 w-full" />
                      </div>
                    ) : confidenceData ? (
                      <>
                        {/* Main Confidence Score */}
                        <div className="bg-card border border-border rounded-sm p-6 text-center">
                          <h4 className="font-medium mb-4 flex items-center justify-center gap-2">
                            <Gauge className="w-5 h-5 text-purple-400" />
                            Platform Confidence Score
                          </h4>
                          <div className="relative inline-block">
                            <div className={`text-6xl font-bold font-mono ${getConfidenceColor(confidenceData.confidence_score)}`}>
                              {confidenceData.confidence_score}
                            </div>
                            <div className="text-2xl text-muted-foreground">/100</div>
                          </div>
                          <p className="text-sm text-muted-foreground mt-2">
                            Confidence Level: <span className="font-medium text-foreground">{confidenceData.confidence_level}</span>
                          </p>
                        </div>

                        {/* Score Breakdown */}
                        <div className="bg-card border border-border rounded-sm p-6">
                          <h4 className="font-medium mb-4">Score Breakdown</h4>
                          <div className="space-y-4">
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-muted-foreground">Probability Margin</span>
                                <span className="font-mono">{confidenceData.score_breakdown?.probability_margin}/40</span>
                              </div>
                              <Progress value={(confidenceData.score_breakdown?.probability_margin / 40) * 100} className="h-2" />
                            </div>
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-muted-foreground">Data Quality</span>
                                <span className="font-mono">{confidenceData.score_breakdown?.data_quality}/30</span>
                              </div>
                              <Progress value={(confidenceData.score_breakdown?.data_quality / 30) * 100} className="h-2" />
                            </div>
                            <div>
                              <div className="flex justify-between text-sm mb-1">
                                <span className="text-muted-foreground">Model Confidence</span>
                                <span className="font-mono">{confidenceData.score_breakdown?.model_confidence}/30</span>
                              </div>
                              <Progress value={(confidenceData.score_breakdown?.model_confidence / 30) * 100} className="h-2" />
                            </div>
                          </div>
                        </div>

                        {/* Risk Assessment */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-card border border-border rounded-sm p-4 text-center">
                            <p className="text-sm text-muted-foreground uppercase mb-2">Volatility</p>
                            <Badge className={
                              confidenceData.volatility === 'High' ? 'bg-red-500/20 text-red-400' :
                              confidenceData.volatility === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-green-500/20 text-green-400'
                            }>
                              {confidenceData.volatility}
                            </Badge>
                          </div>
                          <div className="bg-card border border-border rounded-sm p-4 text-center">
                            <p className="text-sm text-muted-foreground uppercase mb-2">Upset Risk</p>
                            <Badge className={
                              confidenceData.upset_risk === 'High' ? 'bg-red-500/20 text-red-400' :
                              confidenceData.upset_risk === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-green-500/20 text-green-400'
                            }>
                              {confidenceData.upset_risk}
                            </Badge>
                          </div>
                        </div>

                        {/* Platform Accuracy */}
                        <div className="bg-secondary/30 border border-border rounded-sm p-4">
                          <h4 className="font-medium mb-3">Platform Accuracy</h4>
                          <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                              <p className="text-2xl font-bold font-mono text-green-400">
                                {confidenceData.platform_accuracy?.overall}%
                              </p>
                              <p className="text-xs text-muted-foreground">Overall</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold font-mono text-blue-400">
                                {confidenceData.platform_accuracy?.league || 'N/A'}%
                              </p>
                              <p className="text-xs text-muted-foreground">{game.league_name}</p>
                            </div>
                            <div>
                              <p className="text-2xl font-bold font-mono">
                                {confidenceData.platform_accuracy?.total_predictions}
                              </p>
                              <p className="text-xs text-muted-foreground">Predictions</p>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <p className="text-muted-foreground">No confidence data available</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Model Notes */}
              <div className="bg-card border border-border rounded-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Info className="w-5 h-5 text-muted-foreground" />
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                    Model Notes
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  {model_notes}
                </p>
              </div>

              {/* Disclaimer */}
              <div className="bg-red-500/5 border border-red-500/20 rounded-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Shield className="w-5 h-5 text-red-400" />
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight text-red-400">
                    Disclaimer
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  For informational and entertainment purposes only. Not betting advice. 
                  Our probability estimates are statistical projections and should not be 
                  used for gambling decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Prediction Details Modal */}
      <Dialog open={showPredictionModal} onOpenChange={setShowPredictionModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              Platform Score Prediction Details
            </DialogTitle>
            <DialogDescription>
              Comprehensive analysis based on statistical modeling and data analytics
            </DialogDescription>
          </DialogHeader>
          
          {predictionDetails ? (
            <div className="space-y-6 mt-4">
              {/* Predicted Score */}
              <div className="bg-purple-500/10 border border-purple-500/20 rounded-sm p-6 text-center">
                <p className="text-sm text-purple-400 uppercase tracking-wide mb-3">Predicted Final Score</p>
                <div className="flex items-center justify-center gap-8">
                  <div className="text-center">
                    <p className="font-mono text-4xl font-bold">{predictionDetails.predicted_score?.away || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">{predictionDetails.predicted_score?.away_team}</p>
                  </div>
                  <span className="text-2xl text-muted-foreground">-</span>
                  <div className="text-center">
                    <p className="font-mono text-4xl font-bold">{predictionDetails.predicted_score?.home || 0}</p>
                    <p className="text-sm text-muted-foreground mt-1">{predictionDetails.predicted_score?.home_team}</p>
                  </div>
                </div>
              </div>

              {/* Win Probability */}
              <div className="bg-card border border-border rounded-sm p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-blue-400" />
                  Win Probability
                </h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{predictionDetails.predicted_score?.home_team}</span>
                      <span className="font-mono">{predictionDetails.win_probability?.home}%</span>
                    </div>
                    <Progress value={predictionDetails.win_probability?.home || 50} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>{predictionDetails.predicted_score?.away_team}</span>
                      <span className="font-mono">{predictionDetails.win_probability?.away}%</span>
                    </div>
                    <Progress value={predictionDetails.win_probability?.away || 50} className="h-2" />
                  </div>
                </div>
              </div>

              {/* Full Analysis */}
              <div className="bg-card border border-border rounded-sm p-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-green-400" />
                  Detailed Analysis
                </h4>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {predictionDetails.analysis_summary || "Analysis not available."}
                </p>
              </div>

              {/* Prediction Factors */}
              {predictionDetails.factors && Object.keys(predictionDetails.factors).length > 0 && (
                <div className="bg-card border border-border rounded-sm p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Zap className="w-4 h-4 text-yellow-400" />
                    Key Factors
                  </h4>
                  <div className="space-y-2">
                    {Object.entries(predictionDetails.factors).map(([key, value]) => {
                      // Skip rendering if value is null/undefined
                      if (value === null || value === undefined) return null;
                      
                      // Handle object values - render as nested list
                      if (typeof value === 'object' && !Array.isArray(value)) {
                        return (
                          <div key={key} className="flex items-start gap-2 text-sm">
                            <Target className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium capitalize">{key.replace(/_/g, ' ')}</span>
                              <div className="ml-2 mt-1 text-xs text-muted-foreground space-y-0.5">
                                {Object.entries(value).map(([subKey, subValue]) => (
                                  <div key={subKey}>
                                    <span className="capitalize">{subKey.replace(/_/g, ' ')}: </span>
                                    <span>{typeof subValue === 'object' ? JSON.stringify(subValue) : String(subValue)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        );
                      }
                      
                      // Handle array values
                      if (Array.isArray(value)) {
                        return (
                          <div key={key} className="flex items-start gap-2 text-sm">
                            <Target className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="font-medium capitalize">{key.replace(/_/g, ' ')}: </span>
                              <span className="text-muted-foreground">{value.join(', ')}</span>
                            </div>
                          </div>
                        );
                      }
                      
                      // Handle primitive values (string, number, boolean)
                      return (
                        <div key={key} className="flex items-start gap-2 text-sm">
                          <Target className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <span className="font-medium capitalize">{key.replace(/_/g, ' ')}: </span>
                            <span className="text-muted-foreground">{String(value)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Injuries Impact */}
              {predictionDetails.injuries_impact && (
                <div className="bg-card border border-border rounded-sm p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    Injuries Impact
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="font-medium">{predictionDetails.predicted_score?.home_team}</p>
                      <p className="text-muted-foreground">
                        {predictionDetails.injuries_impact.home_out_count || 0} players OUT
                      </p>
                    </div>
                    <div>
                      <p className="font-medium">{predictionDetails.predicted_score?.away_team}</p>
                      <p className="text-muted-foreground">
                        {predictionDetails.injuries_impact.away_out_count || 0} players OUT
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Methodology */}
              {predictionDetails.methodology && (
                <div className="bg-secondary/30 border border-border rounded-sm p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-muted-foreground" />
                    Prediction Methodology
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div>
                      <p className="font-medium text-muted-foreground mb-1">Data Sources:</p>
                      <div className="flex flex-wrap gap-1">
                        {predictionDetails.methodology.data_sources?.map((source) => (
                          <Badge key={`source-${source}`} variant="outline" className="text-xs">{source}</Badge>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground mb-1">Home Advantage Applied:</p>
                      <p className="text-foreground">{predictionDetails.methodology.home_advantage}</p>
                    </div>
                    <div>
                      <p className="font-medium text-muted-foreground mb-1">Injury Impact Formula:</p>
                      <p className="text-foreground">{predictionDetails.methodology.injury_penalty}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 py-8">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Player Prediction Modal */}
      <Dialog open={showPlayerPredictionModal} onOpenChange={setShowPlayerPredictionModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {selectedPlayer?.headshot ? (
                <img 
                  src={selectedPlayer.headshot} 
                  alt={selectedPlayer?.name}
                  className="w-12 h-12 rounded-full object-cover"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-muted-foreground" />
                </div>
              )}
              <div>
                <span className="font-display uppercase tracking-tight">{selectedPlayer?.name || 'Player'}</span>
                <p className="text-sm font-normal text-muted-foreground">
                  {selectedPlayer?.position} {selectedPlayer?.jersey && `#${selectedPlayer.jersey}`}
                </p>
              </div>
            </DialogTitle>
            <DialogDescription>
              Predicted stats for today's game based on season averages and recent form
            </DialogDescription>
          </DialogHeader>
          
          {loadingPlayerPrediction ? (
            <div className="py-8 flex flex-col items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary mb-3" />
              <p className="text-sm text-muted-foreground">Loading predictions...</p>
            </div>
          ) : playerPrediction ? (
            <div className="space-y-4">
              {/* Player Info */}
              {playerPrediction.player_info && (
                <div className="flex items-center gap-3 p-3 bg-secondary/30 rounded-sm">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{playerPrediction.player_info.team}</p>
                    <p className="text-xs text-muted-foreground">
                      {playerPrediction.player_info.height} • {playerPrediction.player_info.weight}
                    </p>
                  </div>
                  <Badge variant="outline">
                    {playerPrediction.prediction_confidence?.toUpperCase()} Confidence
                  </Badge>
                </div>
              )}
              
              {/* Predicted Stats */}
              {playerPrediction.predicted_stats && Object.keys(playerPrediction.predicted_stats).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    Predicted Stats
                  </h4>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {Object.entries(playerPrediction.predicted_stats).slice(0, 8).map(([key, value]) => (
                      <div key={`stat-${key}`} className="text-center p-2 bg-secondary/50 rounded-sm">
                        <p className="text-lg font-bold font-mono">{typeof value === 'number' ? value.toFixed(1) : value}</p>
                        <p className="text-xs text-muted-foreground">{key}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Trend Factors */}
              {playerPrediction.prediction_factors && playerPrediction.prediction_factors.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-400" />
                    Recent Form Trends
                  </h4>
                  <div className="space-y-2">
                    {playerPrediction.prediction_factors.map((factor, idx) => {
                      const trend = getTrendIndicator(factor.trend);
                      const statName = factor.factor.replace('recent_form_', '').toUpperCase();
                      return (
                        <div key={`form-${idx}-${factor.factor}`} className="flex items-center justify-between p-2 bg-secondary/30 rounded-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{trend.emoji}</span>
                            <span className="text-sm font-medium">{statName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-medium ${trend.color}`}>{trend.text}</span>
                            <Badge variant={factor.trend === 'hot' ? 'default' : factor.trend === 'cold' ? 'secondary' : 'outline'}>
                              {factor.impact}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Recent Performance */}
              {playerPrediction.recent_performance && playerPrediction.recent_performance.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                    <History className="w-4 h-4 text-muted-foreground" />
                    Last {playerPrediction.recent_performance.length} Games
                  </h4>
                  <div className="overflow-x-auto">
                    <div className="flex gap-2">
                      {playerPrediction.recent_performance.map((game) => (
                        <div key={game.game_id || `${game.date}-${game.opponent}`} className="flex-shrink-0 text-center p-2 bg-secondary/30 rounded-sm min-w-[60px]">
                          <p className="text-xs text-muted-foreground mb-1">G{game.game}</p>
                          {game.PTS !== undefined && <p className="text-sm font-mono">{game.PTS} pts</p>}
                          {game.REB !== undefined && <p className="text-xs text-muted-foreground">{game.REB} reb</p>}
                          {game.AST !== undefined && <p className="text-xs text-muted-foreground">{game.AST} ast</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* AI Impact Note */}
              {playerPrediction.ai_impact_data && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-sm">
                  <div className="flex items-center gap-2 mb-1">
                    <Brain className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">AI Learning Data</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Our AI tracks this player's historical impact on game outcomes to improve prediction accuracy.
                  </p>
                </div>
              )}
              
              {/* View Full Stats Button */}
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setShowPlayerPredictionModal(false);
                    navigate('/stats', { 
                      state: { 
                        openPlayer: true,
                        playerId: selectedPlayer?.player_id || selectedPlayer?.id || playerPrediction?.player_id,
                        playerName: selectedPlayer?.name || playerPrediction?.player_info?.name,
                        teamName: playerPrediction?.player_info?.team || '',
                        leagueId: gameData?.game?.league_id || 'nba',
                        fromGame: gameId
                      }
                    });
                  }}
                  data-testid="view-full-stats-btn"
                >
                  <BarChart3 className="w-4 h-4 mr-2" />
                  View Full Player Stats
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground text-center pt-2 border-t border-border">
                {playerPrediction.note}
              </p>
            </div>
          ) : (
            <div className="py-8 text-center">
              <AlertCircle className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">No prediction data available for this player</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
}

function PremiumLock() {
  const navigate = useNavigate();
  
  return (
    <div className="relative bg-card border border-border rounded-sm p-6">
      <div className="premium-blur">
        <div className="h-40 flex items-center justify-center">
          <p className="text-muted-foreground">Premium content preview</p>
        </div>
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center bg-card/80 rounded-sm">
        <Lock className="w-12 h-12 text-muted-foreground mb-4" />
        <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-2">
          Premium Content
        </h3>
        <p className="text-sm text-muted-foreground text-center mb-4 max-w-xs">
          Subscribe to access detailed trends, head-to-head analysis, and more.
        </p>
        <Button 
          className="btn-blaze rounded-sm"
          onClick={() => navigate('/billing')}
        >
          Subscribe - $20/month
        </Button>
      </div>
    </div>
  );
}
